import { FC, SyntheticEvent, useMemo, useState } from 'react';
import { Autocomplete } from '@mui/material';
import { Entity } from 'api/domain/types';
import { debounce, get } from 'lodash';
import { useFormikContext } from 'formik';

import { ID } from 'domain/types/ID';
import { useCaseSearchPriceLists } from 'application/prices/useCases/useCaseSearchPriceLists';
import { useTranslationPrefix } from 'infrastructure/translations/i18n';
import DEFAULT_DEBOUNCE_TIME from 'infrastructure/utils/defaultDebounceTime';
import { FormikTextField } from 'targets/web/modules/dashboard/components';

const PriceListField: FC<{ disabled?: boolean; optional?: boolean }> = ({ disabled, optional }) => {
  const { values, errors, touched, initialValues, setFieldValue } = useFormikContext<{
    portalPriceList?: { id: ID; name: string };
  }>();
  const t = useTranslationPrefix('configuration.customer_details.customer_form');

  const [priceListPhrase, setPriceListPhrase] = useState('');
  const { allPages: items, isLoading } = useCaseSearchPriceLists({
    phrase: priceListPhrase,
  });

  const priceLists = useMemo(() => {
    if (!initialValues.portalPriceList?.id) {
      return items;
    }

    if (!items.find(({ id }) => id === initialValues.portalPriceList?.id)) {
      return [initialValues.portalPriceList].concat(items);
    }

    return items;
  }, [initialValues.portalPriceList, items]);

  const handleInputChange = useMemo(
    () =>
      debounce((event: SyntheticEvent, newInputValue: string) => {
        if (event?.type !== 'change' && newInputValue) {
          return;
        }

        setPriceListPhrase(newInputValue);
      }, DEFAULT_DEBOUNCE_TIME),
    [setPriceListPhrase],
  );

  return (
    <Autocomplete<Entity<{ name: string }>, false, false, boolean>
      loading={isLoading}
      disabled={disabled}
      options={priceLists}
      value={values.portalPriceList?.id ? values.portalPriceList : null}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      filterOptions={(x) => x}
      onInputChange={handleInputChange}
      data-testname="portalPriceList"
      onChange={(_, value) =>
        setFieldValue(
          'portalPriceList',
          typeof value === 'string' ? { name: value, id: value } : value,
        )
      }
      renderInput={(params) => {
        const hasError = !!get(errors, 'portalPriceList') && !!get(touched, 'portalPriceList');

        return (
          <FormikTextField
            {...params}
            name="portalPriceList"
            inputProps={{
              ...params.inputProps,
              required: !optional,
            }}
            error={hasError}
            helperText={hasError ? t('error') : undefined}
            label={t('price_list_label')}
            required={!optional}
          />
        );
      }}
    />
  );
};

export default PriceListField;
